/**
 * PR previews are Firebase Hosting preview channels on the dev site:
 * https://iteleport-nightly--pr<n>-<8-char hash>.web.app. A "shared" preview is the PR's client running
 * against the regular dev backend; dev's backend, floof fleet and desktop app accept these origins.
 *
 * Dependency-free on purpose: loaded by the client, cloud functions, floof and the desktop shell.
 */

/**
 * A `pr<n>` Hosting preview channel on the dev site (previewChannelIdForPr), plus the 8-character
 * suffix Firebase appends. Exact on purpose: site IDs are at most 30 characters, so no other
 * Firebase site can be named `iteleport-nightly--pr<n>-<8 chars>` and serve a lookalike origin.
 */
export const devPreviewChannelOriginPattern = /^https:\/\/iteleport-nightly--pr\d+-[a-z0-9]{8}\.web\.app$/;

const prChannelOriginPattern = /^https:\/\/iteleport-nightly--pr(\d+)-[a-z0-9]{8}\.web\.app$/;

/** An isolated preview's own RTDB instance in the dev project. */
export const devPreviewDatabaseUrlPattern = /^https:\/\/iteleport-nightly-pr\d+\.firebaseio\.com$/;

export function previewRtdbInstanceForPr(pr: number): string {
  return `iteleport-nightly-${previewChannelIdForPr(pr)}`;
}

export function previewDatabaseUrlForPr(pr: number): string {
  return `https://${previewRtdbInstanceForPr(pr)}.firebaseio.com`;
}

export function previewIdFromDatabaseUrl(databaseUrl: unknown): string | undefined {
  if (typeof databaseUrl !== 'string') return undefined;
  const match = /^https:\/\/iteleport-nightly-(pr([1-9]\d*))\.firebaseio\.com$/.exec(databaseUrl);
  return match && match[0] === databaseUrl && Number.isSafeInteger(Number(match[2])) ? match[1] : undefined;
}

export function isDevPreviewChannelOrigin(origin: unknown): origin is string {
  return typeof origin === 'string' && devPreviewChannelOriginPattern.test(origin);
}

/** The PR number of a `pr<n>` preview channel origin, if it is one. */
export function previewPrFromOrigin(origin: unknown): number | undefined {
  if (typeof origin !== 'string') return undefined;
  const match = prChannelOriginPattern.exec(origin);
  return match ? Number(match[1]) : undefined;
}

export function previewChannelIdForPr(pr: number): string {
  if (!Number.isInteger(pr) || pr < 1) throw new Error(`Invalid PR number: ${pr}`);
  return `pr${pr}`;
}
